import { Button, ButtonGroup, Grid, GridItem, type StackProps, VStack, useToast } from "@chakra-ui/react";
import { useDisclosure } from "@equidefi/hooks";
import { Input, Password } from "@equidefi/ui/forms";
import { Icon } from "@equidefi/ui/icon";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FC, type ReactNode, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router";
import { z } from "zod";

import { COUNTRY_OPTIONS } from "@equidefi/constants/countries";
import {
  ENTITY,
  ENTITY_SUB_TYPE,
  ENTITY_SUB_TYPE_OPTIONS,
  INDIVIDUAL,
  JOINT,
  JOINT_SUB_TYPE_KEYS,
  JOINT_SUB_TYPE_OPTIONS,
  TRUST,
  TRUST_SUB_TYPE_KEYS,
  TRUST_SUB_TYPE_OPTIONS,
} from "@equidefi/constants/profile";
import { ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { extractErrorMessage } from "@equidefi/portals";
import { postalCodeZodRefinement } from "@equidefi/portals/helpers/postalCode";
import { useInvestmentNoteCreate } from "@equidefi/portals/hooks/useNotes";
import { usePostalCodeExcluded } from "@equidefi/portals/hooks/usePostalCode";
import { useUpdateVault } from "@equidefi/portals/hooks/useVault";
import { offeringIsTypeRegA } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { Heading, Paper, SingleSelect, Text } from "@equidefi/ui";

import { MESSAGE } from "@/constants/forms";
import { useOfferingContext } from "../Offerings/OfferingContext";
import { useInvestmentContext } from "./InvestmentContext";
import { ConfirmChangesModal } from "./modals/ConfirmChangesModal";

const string = (message: string) => z.string({ message: message }).trim().min(1, { message: message });
const asciiString = (message: string) =>
  string(message).refine((val) => ASCII_ONLY_REGEX.test(val), {
    message: MESSAGE.ASCII_ONLY,
  });

const baseSchema = z.object({
  accredited: z.boolean({
    message: MESSAGE.REQUIRED("Accredited"),
  }),
  broker_dealer: z.boolean({
    message: MESSAGE.REQUIRED("Broker-Dealer"),
  }),
});

const entitySchema = z.object({
  entity_type: z.literal(ENTITY),
  company: asciiString(MESSAGE.REQUIRED("Entity Name")),
  ein: string(MESSAGE.REQUIRED("EIN")),
  entity_address_street_1: string(MESSAGE.REQUIRED("Street")),
  entity_address_street_2: z.string().optional().nullable(),
  entity_address_city: string(MESSAGE.REQUIRED("City")),
  entity_address_state: string(MESSAGE.REQUIRED("State")),
  entity_address_postal_code: z.string().trim().optional().nullable(),
  entity_address_country_code: string(MESSAGE.REQUIRED("Country")).max(2, {
    message: "Must be 2 letters",
  }),
});

const regASchema = z.object({
  annual_income: z
    .string({
      message: MESSAGE.REQUIRED("Annual Income"),
    })
    .min(1, "Must be more than 0"),
  net_worth: z
    .string({
      message: MESSAGE.REQUIRED("Net Worth"),
    })
    .min(1, { message: "Must be more than 0" }),
});

const buildSchema = (offering?: DTO.Offering) => {
  if (!offering) return baseSchema;

  const isRegA = offeringIsTypeRegA(offering);

  let schema = baseSchema;
  if (isRegA) {
    schema = schema.merge(regASchema);
  }

  return z
    .discriminatedUnion("account_type", [
      z
        .object({
          account_type: z.literal(ENTITY),
          ta_sub_type: z.enum(ENTITY_SUB_TYPE, {
            message: MESSAGE.REQUIRED("Sub-Type"),
          }),
        })
        .merge(entitySchema)
        .merge(schema),

      z
        .object({
          account_type: z.literal(TRUST),
          ta_sub_type: z.enum(TRUST_SUB_TYPE_KEYS, {
            message: MESSAGE.REQUIRED("Sub-Type"),
          }),
        })
        .merge(entitySchema)
        .merge(schema),

      z
        .object({
          account_type: z.literal(JOINT),
          entity_type: z.literal(INDIVIDUAL),
          ta_sub_type: z.enum(JOINT_SUB_TYPE_KEYS, {
            message: MESSAGE.REQUIRED("Sub-Type"),
          }),
        })
        .merge(schema),

      z
        .object({
          account_type: z.literal(INDIVIDUAL),
          entity_type: z.literal(INDIVIDUAL),
        })
        .merge(schema),
    ])
    .superRefine(
      postalCodeZodRefinement({
        countryField: "entity_address_country_code",
        postalField: "entity_address_postal_code",
        message: MESSAGE.REQUIRED("Postal Code"),
        when: (data) => data.account_type === ENTITY || data.account_type === TRUST,
      }),
    );
};

type AccountTypeSchema = z.infer<ReturnType<typeof buildSchema>>;

const yesNoOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const AccountTypeCard: FC<StackProps & { icon: ReactNode; label: string; isActive: boolean }> = (props) => {
  const { isActive, icon, label, ...rest } = props;

  return (
    <VStack
      p={4}
      borderRadius="lg"
      borderWidth={isActive ? "2px" : "1px"}
      border="solid"
      borderColor={isActive ? "purple.500" : "gray.200"}
      bg={isActive ? "purple.50" : "white"}
      justify="center"
      align="center"
      spacing={2}
      cursor="pointer"
      {...rest}>
      {icon}
      <Text textStyle="body" mb="0">
        {label}
      </Text>
    </VStack>
  );
};

export default function EditAccountType(): React.JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { investment } = useInvestmentContext();
  const { offering } = useOfferingContext();
  const updateVault = useUpdateVault(investment?.vault?.id as string, investment?.id as string);
  const confirmModal = useDisclosure();
  const [updateData, setUpdateData] = useState<AccountTypeSchema>();
  const createNote = useInvestmentNoteCreate(investment?.id);

  const onConfirm = async (reason: string) => {
    try {
      await updateVault.mutateAsync(updateData);
      toast({
        status: "success",
        description: "Successfully updated investor's account data.",
      });
      confirmModal.onClose();
      await createNote.mutateAsync({
        content: `**Account Type Updated**: ${reason}`,
      });
      navigate("../", { state: { refetch: true } });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: extractErrorMessage(e),
      });
    }
  };

  const onSubmit = (data: AccountTypeSchema) => {
    confirmModal.onOpen();
    setUpdateData(data);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<DTO.Vault>({
    defaultValues: {
      entity_type: investment?.vault?.entity_type ?? INDIVIDUAL,
      account_type: investment?.vault?.account_type ?? INDIVIDUAL,
      ta_sub_type: investment?.vault?.ta_sub_type,
      company: investment?.vault?.company,
      ein: investment?.vault?.ein,
      entity_address_street_1: investment?.vault?.entity_address_street_1,
      entity_address_street_2: investment?.vault?.entity_address_street_2,
      entity_address_city: investment?.vault?.entity_address_city,
      entity_address_state: investment?.vault?.entity_address_state,
      entity_address_postal_code: investment?.vault?.entity_address_postal_code,
      entity_address_country_code: investment?.vault?.entity_address_country_code,
      accredited: investment?.vault?.accredited,
      broker_dealer: investment?.vault?.broker_dealer,
      annual_income: investment?.vault?.annual_income,
      net_worth: investment?.vault?.net_worth,
    },
    resolver: zodResolver(buildSchema(offering)),
  });

  const accountType = watch("account_type");
  const hidePostalCode = usePostalCodeExcluded(
    watch("entity_address_country_code"),
    watch("entity_address_postal_code"),
    () => setValue("entity_address_postal_code", ""),
  );
  const subTypeOptions = useMemo(() => {
    switch (accountType) {
      case ENTITY:
        return ENTITY_SUB_TYPE_OPTIONS;
      case TRUST:
        return TRUST_SUB_TYPE_OPTIONS;
      case JOINT:
        return JOINT_SUB_TYPE_OPTIONS;
      default:
        return [];
    }
  }, [accountType]);

  const isIndividualOrJoint = useMemo(() => {
    return accountType === INDIVIDUAL || accountType === JOINT;
  }, [accountType]);

  const isAccountTypeChanged = investment.vault.account_type !== accountType;

  return (
    <VStack justify="flex-start" align="start" gap={2} flex={2} w="full">
      <Button variant="link" colorScheme="blue" onClick={() => navigate("../")} gap="2">
        <Icon.ArrowLeft /> Back to Investors Tab
      </Button>
      <Paper p="5" w="full">
        <Heading fontWeight="bold">Change Account Type</Heading>

        <Text color="gray.500" mb={4}>
          This is passed on to the transfer agent and determines how{" "}
          {offering?.security_type_plural?.toLowerCase() || "shares"} will be issued.
        </Text>
        <Grid
          templateColumns={{
            base: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)",
          }}
          gap={4}
          mb={4}>
          <AccountTypeCard
            icon={<Icon.User size="1.5rem" />}
            label="Individual"
            isActive={accountType === INDIVIDUAL}
            onClick={() => {
              setValue("account_type", INDIVIDUAL);
              setValue("entity_type", INDIVIDUAL);
              setValue("ta_sub_type", undefined);
            }}
          />
          <AccountTypeCard
            icon={<Icon.Users size="1.5rem" />}
            label="Joint Tenants"
            isActive={accountType === JOINT}
            onClick={() => {
              setValue("account_type", JOINT);
              setValue("entity_type", INDIVIDUAL);
              setValue("ta_sub_type", undefined);
            }}
          />
          <AccountTypeCard
            icon={<Icon.FileText size="1.5rem" />}
            label="Trust"
            isActive={accountType === TRUST}
            onClick={() => {
              setValue("account_type", TRUST);
              setValue("entity_type", ENTITY);
              setValue("ta_sub_type", undefined);
            }}
          />
          <AccountTypeCard
            icon={<Icon.Briefcase size="1.5rem" />}
            label="Entity"
            isActive={accountType === ENTITY}
            onClick={() => {
              setValue("account_type", ENTITY);
              setValue("entity_type", ENTITY);
              setValue("ta_sub_type", undefined);
            }}
          />
        </Grid>

        {subTypeOptions.length > 0 && (
          <SingleSelect<DTO.Vault>
            label="Account Sub-Type"
            control={control}
            options={subTypeOptions}
            error={errors.ta_sub_type?.message}
            {...register("ta_sub_type")}
            onChange={({ value }) => setValue("ta_sub_type", value)}
          />
        )}

        {!isIndividualOrJoint && (
          <>
            <Heading fontWeight="bold" mt="4">
              Edit Entity Details
            </Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem colSpan={1}>
                <Input
                  label="Entity Name"
                  isRequired
                  autoComplete="off"
                  isInvalid={!!errors.company}
                  error={errors.company?.message}
                  {...register("company")}
                />
              </GridItem>
              <GridItem colSpan={1}>
                <Password
                  label="EIN"
                  isRequired
                  maxLength={9}
                  error={errors.ein?.message}
                  isInvalid={!!errors.ein}
                  mt="1"
                  borderRadius="5"
                  checkboxHintText="Show EIN"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  {...register("ein")}
                />
              </GridItem>
            </Grid>

            <Heading fontWeight="bold" mt="4">
              Entity Address
            </Heading>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem colSpan={1}>
                <Input
                  label="Street 1"
                  isRequired
                  isInvalid={!!errors.entity_address_street_1}
                  error={errors.entity_address_street_1?.message}
                  {...register("entity_address_street_1")}
                />
              </GridItem>
              <GridItem colSpan={1}>
                <Input
                  label="Street 2"
                  isInvalid={!!errors.entity_address_street_2}
                  error={errors.entity_address_street_2?.message}
                  {...register("entity_address_street_2")}
                />
              </GridItem>
              <GridItem colSpan={1}>
                <Input
                  label="City"
                  isRequired
                  isInvalid={!!errors.entity_address_city}
                  error={errors.entity_address_city?.message}
                  {...register("entity_address_city")}
                />
              </GridItem>
              <GridItem colSpan={1}>
                <Input
                  label="State"
                  isRequired
                  isInvalid={!!errors.entity_address_state}
                  maxLength={2}
                  error={errors.entity_address_state?.message}
                  {...register("entity_address_state")}
                />
              </GridItem>
              {!hidePostalCode && (
                <GridItem colSpan={1}>
                  <Input
                    label="Postal Code"
                    isRequired
                    isInvalid={!!errors.entity_address_postal_code}
                    error={errors.entity_address_postal_code?.message}
                    {...register("entity_address_postal_code")}
                  />
                </GridItem>
              )}
              <GridItem colSpan={1}>
                <SingleSelect<DTO.Vault>
                  label="Country"
                  isRequired
                  control={control}
                  options={COUNTRY_OPTIONS}
                  error={errors.entity_address_country_code?.message}
                  {...register("entity_address_country_code")}
                  onChange={({ value }) => setValue("entity_address_country_code", value)}
                />
              </GridItem>
            </Grid>
          </>
        )}

        <Heading fontWeight="bold" mt="4">
          Metadata
        </Heading>
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem colSpan={1}>
            <SingleSelect<DTO.Vault>
              label="Is this investor accredited?"
              control={control}
              options={yesNoOptions}
              error={errors.accredited?.message}
              {...register("accredited")}
              onChange={({ value }) => setValue("accredited", value)}
            />
          </GridItem>
          <GridItem colSpan={1}>
            <SingleSelect<DTO.Vault>
              label="Is this investor affiliated with a broker-dealer?"
              control={control}
              options={yesNoOptions}
              error={errors.broker_dealer?.message}
              {...register("broker_dealer")}
              onChange={({ value }) => setValue("broker_dealer", value)}
            />
          </GridItem>
          {offeringIsTypeRegA(offering as DTO.Offering) && (
            <>
              <GridItem colSpan={1}>
                <Controller
                  control={control}
                  name="annual_income"
                  render={({ field: { onChange, value }, fieldState }) => (
                    <Input
                      as={NumericFormat}
                      label={isIndividualOrJoint ? "Annual Income" : "Revenue"}
                      name="annual_income"
                      defaultValue={value ?? 0}
                      prefix="$"
                      // @ts-ignore
                      allowNegative={false}
                      allowLeadingZeros={false}
                      isInvalid={fieldState.invalid}
                      error={fieldState.error?.message}
                      thousandSeparator={true}
                      onValueChange={(val: {
                        floatValue: number;
                        value: string;
                        formattedValue: "string";
                      }) => {
                        onChange(val?.value);
                      }}
                    />
                  )}
                />
              </GridItem>
              <GridItem colSpan={1}>
                <Controller
                  control={control}
                  name="net_worth"
                  render={({ field: { onChange, value }, fieldState }) => (
                    <Input
                      as={NumericFormat}
                      label={isIndividualOrJoint ? "Net Worth" : "Net Assets"}
                      name="net_worth"
                      defaultValue={value ?? 0}
                      prefix="$"
                      // @ts-ignore
                      allowNegative={false}
                      allowLeadingZeros={false}
                      isInvalid={fieldState.invalid}
                      error={fieldState.error?.message}
                      thousandSeparator={true}
                      onValueChange={(val: {
                        floatValue: number;
                        value: string;
                        formattedValue: "string";
                      }) => {
                        onChange(val?.value);
                      }}
                    />
                  )}
                />
              </GridItem>
            </>
          )}
        </Grid>

        <ButtonGroup mt="4" spacing="4">
          <Button isLoading={updateVault.isLoading} onClick={handleSubmit(onSubmit)} colorScheme="purple">
            Save Changes
          </Button>
          <Button onClick={() => navigate("../")} variant="outline">
            Discard Changes
          </Button>
        </ButtonGroup>
      </Paper>

      <ConfirmChangesModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.onClose}
        isAccountTypeChanged={isAccountTypeChanged}
        onConfirm={async (reason) => {
          onConfirm(reason);
        }}
      />
    </VStack>
  );
}
